import type { ScenarioSummary } from "@/api/types";

export interface ScenarioCategory {
  id: string;
  label: string;
  /** Tailwind classes for the badge — keep semantic, theme-aware. */
  className: string;
}

/**
 * Industry is derived from the scenario slug prefix (everything up to the
 * first dash). Scenarios drift in and out of these buckets as we add them,
 * so we keep the mapping flexible and fall back to "Other" rather than
 * forcing a categorization.
 */
export const INDUSTRIES: ScenarioCategory[] = [
  { id: "banking", label: "Banking", className: "bg-blue-500/15 text-blue-600 dark:text-blue-300 border-blue-500/30" },
  { id: "fintech", label: "FinTech", className: "bg-blue-500/15 text-blue-600 dark:text-blue-300 border-blue-500/30" },
  { id: "capx", label: "Capital Markets", className: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-300 border-cyan-500/30" },
  { id: "ecommerce", label: "E-commerce", className: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  { id: "retail", label: "Retail", className: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300 border-yellow-500/30" },
  { id: "gaming", label: "Gaming", className: "bg-purple-500/15 text-purple-600 dark:text-purple-300 border-purple-500/30" },
  { id: "adtech", label: "AdTech", className: "bg-pink-500/15 text-pink-600 dark:text-pink-300 border-pink-500/30" },
  { id: "energy", label: "Energy", className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30" },
  { id: "ais", label: "Travel & Logistics", className: "bg-teal-500/15 text-teal-600 dark:text-teal-300 border-teal-500/30" },
  { id: "checkout", label: "Checkout", className: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  { id: "data", label: "Data", className: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 border-indigo-500/30" },
  { id: "event", label: "Streaming", className: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 border-indigo-500/30" },
];

const OTHER: ScenarioCategory = {
  id: "other",
  label: "Other",
  className:
    "bg-muted text-muted-foreground border-border",
};

/**
 * Failure kind is best-effort, derived from the slug suffix. Used as a
 * secondary chip on each card to hint at the symptom category.
 */
const FAILURE_PATTERNS: Array<{ match: RegExp; label: string }> = [
  { match: /outage/i, label: "Outage" },
  { match: /latency|slow|timeout/i, label: "Latency" },
  { match: /leak|memory/i, label: "Memory leak" },
  { match: /overflow|backpressure|lag/i, label: "Backpressure" },
  { match: /pause|gc/i, label: "GC pause" },
  { match: /fraud|breach/i, label: "Security" },
  { match: /pipeline|stream|ingest/i, label: "Pipeline" },
];

export function scenarioIndustry(s: ScenarioSummary): ScenarioCategory {
  const prefix = s.slug.split("-")[0];
  return INDUSTRIES.find((i) => i.id === prefix) ?? OTHER;
}

export function scenarioFailureKind(s: ScenarioSummary): string | null {
  const haystack = `${s.slug} ${s.description}`;
  for (const p of FAILURE_PATTERNS) {
    if (p.match.test(haystack)) return p.label;
  }
  return null;
}

/** Unique industries actually represented in `items`, sorted by label. */
export function presentIndustries(items: ScenarioSummary[]): ScenarioCategory[] {
  const seen = new Map<string, ScenarioCategory>();
  for (const s of items) {
    const i = scenarioIndustry(s);
    seen.set(i.id, i);
  }
  return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export function matchesQuery(s: ScenarioSummary, query: string): boolean {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    s.slug.toLowerCase().includes(q) ||
    s.name.toLowerCase().includes(q) ||
    s.description.toLowerCase().includes(q) ||
    (s.stacks ?? []).some((t) => t.toLowerCase().includes(q))
  );
}

/**
 * Display labels for known service languages — fed by the backend's
 * `stacks` field. Anything not in the map renders with its raw value.
 */
const STACK_LABELS: Record<string, string> = {
  java: "Java",
  kotlin: "Kotlin",
  scala: "Scala",
  python: "Python",
  go: "Go",
  node: "Node.js",
  nodejs: "Node.js",
  typescript: "TypeScript",
  rust: "Rust",
  ruby: "Ruby",
  cpp: "C++",
  csharp: "C#",
  dotnet: ".NET",
  postgres: "PostgreSQL",
  postgresql: "PostgreSQL",
  mysql: "MySQL",
  mariadb: "MariaDB",
  mongodb: "MongoDB",
  cassandra: "Cassandra",
  dynamodb: "DynamoDB",
  redis: "Redis",
  memcached: "Memcached",
  elasticsearch: "Elasticsearch",
  opensearch: "OpenSearch",
  kafka: "Kafka",
  rabbitmq: "RabbitMQ",
  nats: "NATS",
  nginx: "nginx",
  envoy: "Envoy",
  haproxy: "HAProxy",
  flink: "Flink",
  spark: "Spark",
  airflow: "Airflow",
  mainframe: "Mainframe",
  cobol: "COBOL",
};

export function stackLabel(raw: string): string {
  return STACK_LABELS[raw.toLowerCase()] ?? raw;
}

/** Unique tech-stack tags across `items`, sorted by display label. */
export function presentStacks(items: ScenarioSummary[]): string[] {
  const seen = new Set<string>();
  for (const s of items) {
    for (const t of s.stacks ?? []) seen.add(t.toLowerCase());
  }
  return Array.from(seen).sort((a, b) => stackLabel(a).localeCompare(stackLabel(b)));
}

export function scenarioHasStack(s: ScenarioSummary, stack: string): boolean {
  return (s.stacks ?? []).some((t) => t.toLowerCase() === stack.toLowerCase());
}
